import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import Alert from "../../../alert";
import bottyCheckingIcon from '../../../../../../../../assets/images/ai-onboarding/botty-checking.svg';
import bottyHasIssues from '../../../../../../../../assets/images/ai-onboarding/botty-has-issues.svg';
import { Row, Col, Spinner, Button } from "reactstrap";
import ProgressCalculator from "../../../progress-calculator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { EMPTY_LIST } from "src/common/constants";
import AIOnboardingService from "src/modules/3rd-party-management/apis/admin/AIOnboardingService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import ConfirmImportLoading from "../confirmation/components/ConfirmImportLoading";

const ProcessingProducts = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [ fetchFinalizeStatus, setFetchFinalizeStatus ] = useState(true);
    const [ fetchFinalizationProgressData, setFetchFinalizationprogressData ] = useState(false);

    const {
        data: finalizeStatusData,
        isFetching: finalizeStatusIsFetching,
        isFetched: finalizeStatusIsFetched,
        isError: fetchFinalizeStatusFaild
    } = useQuery({
        queryKey: ['3rd-party-management-ai-onboarding-get-finalize-status'],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();

            return await service.getFinalizeStatus();
        },
        cacheTime: 0,
        retry: 0,
        refetchOnWindowFocus: false,
        refetchInterval: 10000,
        enabled: fetchFinalizeStatus,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            });
        }
    });

    const {
        data: finalizationProgressData
    } = useQuery({
        queryKey: ['3rd-party-management-ai-onboarding-get-finalization-progress'],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();

            return await service.getFinalizationProgress();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: fetchFinalizationProgressData,
        refetchInterval: 10000,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            });
        }
    });

    const {
        data: processingLogs,
        isFetching: logsAreFetching,
        isFetched: logsFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-ai-onboarding-products-processing-logs'
        ],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();
            return await service.getProductsProcessingingLogs();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: fetchFinalizationProgressData,
        refetchInterval: 10000,
        onError: () => {
            toast(t('An error occurred while retrieving the processing logs.'), {
                type: 'error'
            })
        }
    })

    useEffect(() => {
        if (finalizeStatusData?.status === 'SUCCESS' || finalizeStatusData?.status === 'FAILURE') {
            setFetchFinalizeStatus(false);
            setFetchFinalizationprogressData(true);
        }
    }, [
        finalizeStatusData
    ]);

    useEffect(() => {
        if (finalizationProgressData?.status === 'completed') {
            setFetchFinalizationprogressData(false);
        }
    }, [
        finalizationProgressData
    ]);

    if (
        fetchFinalizeStatus ||
        (!('status' in (finalizationProgressData || {})) || finalizationProgressData.status === 'pending') 
    ) {
        return (
            <>
                <Alert
                    icon={bottyCheckingIcon}
                    title='I’m analyzing your product files'
                    message={(
                        <>
                            <p>
                                {t('This may take a little while')} — {t('please be patient while I check your data for completeness and consistency.')}
                            </p>
                        </>
                    )}
                />

                <div className="ai-onboarding-content-body">
                    <ConfirmImportLoading />

                    <div className="ai-onboarding-tab-actions">
                        <div
                            className="d-flex justify-content-center align-items-center gap-2"
                        >
                            <Button
                                outline
                                color="secondary"
                                onClick={onBackButtonClicked}
                                disabled
                            >
                                {t('Back')}
                            </Button>

                            <Button
                                color="primary"
                                disabled
                            >
                                {t('Next')}
                            </Button>
                        </div>

                        <Button
                            color="secondary"
                            outline
                            onClick={onCancelButtonClicked}
                        >
                            {t('Cancel')}
                        </Button>
                    </div>
                </div>
            </>
        )
    }

    if (fetchFinalizeStatusFaild || finalizeStatusData?.status === 'FAILURE') {
        return (
            <>
                <Alert
                    icon={bottyHasIssues}
                    title='Product Validation Failed'
                    message={(
                        <p>
                            {t('The product file could not be successfully validated. Please review the file content and ensure it follows the required format before trying again.')}
                        </p>
                    )}
                />

                <div className="ai-onboarding-content-body">
                    <div className="ai-onboarding-tab-actions">
                        <div
                            className="d-flex justify-content-center align-items-center gap-2"
                        >
                            <Button
                                outline
                                color="secondary"
                                onClick={onBackButtonClicked}>
                                {t('Back')}
                            </Button>

                            <Button
                                color="primary"
                            >
                                {t('Next')}
                            </Button>
                        </div>

                        <Button
                            color="secondary"
                            outline
                            onClick={onCancelButtonClicked}
                        >
                            {t('Cancel')}
                        </Button>
                    </div>
                </div>
            </>
        )
    }

    const NoDataIndication = () => (
        (logsFetched && !processingLogs?.length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const tableColumns = [
        {
            dataField: "id",
            text: t("SIP ID"),
            sort: true,
            key: 1
        },
        {
            dataField: "product_name",
            text: t("Product Name"),
            sort: true,
            key: 2
        },
        {
            dataField: "nace_code",
            text: t("Nace Code"),
            sort: false,
            key: 3
        },
        {
            dataField: "HS",
            text: t("HS Code"),
            sort: true,
            key: 4,
            formatter: (cell, { data }) => {
                return data?.['HS'];
            }
        },
        {
            dataField: "KN",
            text: t("KN Code"),
            sort: true,
            key: 5,
            formatter: (cell, { data }) => {
                return data?.['KN'];
            }
        },
        {
            dataField: "lot_number",
            text: t("Lot Number"),
            sort: true,
            key: 6,
            formatter: (cell, { data }) => {
                return data?.['Lot Nr'];
            }
        },
        {
            dataField: "financial_year",
            text: t("Financial Year"),
            sort: false,
            key: 7,
            formatter: (cell, { data }) => {
                return data?.['financial year'];
            }
        },
        {
            dataField: "description",
            text: t("Error"),
            sort: false,
            key: 8,
            formatter: (cellContent) => {
                return (
                    <p className="text-danger">
                        {cellContent}
                    </p>
                )
            }
        },
    ]

    const totalRecordsCount = parseInt(finalizationProgressData?.total_rows || 0)
    const processedCount = parseInt(finalizationProgressData?.products_created || 0)
    const rejectedCount = parseInt(finalizationProgressData?.products_failed || 0)
    const waitingCount = totalRecordsCount - (processedCount + rejectedCount);

    const nextButtonIsDisabled = finalizationProgressData?.status !== 'completed'

    return (
           <>
               <Alert
                   icon={bottyCheckingIcon}
                   title='Products Import in Progress'
                   message={t('The import has started and is being processed in the background. You can continue working — the latest status will be shown when you return.')}
               />
   
               <div className="ai-onboarding-content-body">
                   <Row>
                       <Col sm='12' className="mt-2 mb-4">
                           <ProgressCalculator
                               totalCount={totalRecordsCount}
                               processedCount={processedCount}
                               rejectedCount={rejectedCount}
                               waitingCount={waitingCount}
                           />
                       </Col>
   
                       <Col sm='12'>
                           <PaginationProvider
                               pagination={
                                   paginationFactory({
                                       custom: true,
                                       page: 1,
                                       sizePerPage: 25,
                                       totalSize: processingLogs?.length || 0,
                                       withFirstAndLast: true,
                                       alwaysShowAllBtns: true,
                                       prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                       nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                       prePageTitle: t('Pre page'),
                                       firstPageTitle: t('First page'),
                                       lastPageTitle: t('Last page'),
                                       showTotal: false,
                                       paginationTotalRenderer: (from, to, size) => {
                                           return (
                                               <span className="react-bootstrap-table-pagination-total">
                                                   {`${t('Total Items')} ${size}`}
                                               </span>
                                           )
                                       },
                                       hideSizePerPage: false,
                                       sizePerPageList: [
                                           {
                                               text: '25',
                                               value: 25
                                           },
                                           {
                                               text: '50',
                                               value: 50
                                           },
                                           {
                                               text: '100',
                                               value: 100
                                           }
                                       ]
                                   })
                               }>
                               {
                                   ({
                                       paginationProps,
                                       paginationTableProps
                                   }) => (
                                       <>
                                           <Row>
                                               <Col sm="12">
                                                   <h5 className="mt-4">{t('Import Log')} – {t('Failed Entries')}</h5>
                                                   <p className="text-muted">
                                                       {t('Only entries that could not be imported are shown below.')}
                                                   </p>
                                                   <BootstrapTable
                                                       loading={logsAreFetching}
                                                       overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                       defaultSorted={[]}
                                                       keyField={"id"}
                                                       responsive
                                                       bordered={false}
                                                       data={processingLogs || []}
                                                       striped={true}
                                                       columns={tableColumns}
                                                       wrapperClasses="table-responsive"
                                                       classes={"table tpdd-table"}
                                                       headerWrapperClasses={"thead-light"}
                                                       noDataIndication={() => <NoDataIndication />}
                                                       {...paginationTableProps}
                                                   />
                                               </Col>
                                           </Row>
   
                                           <Row>
                                               <Col sm="12">
                                                   <div className="tpdd-pagination-style-1">
                                                       <PaginationTotalStandalone
                                                           {...paginationProps}
                                                       />
   
                                                       <div>
                                                           <PaginationListStandalone
                                                               {...paginationProps}
                                                           />
   
                                                           <SizePerPageDropdownStandalone
                                                               {...paginationProps}
                                                           />
                                                       </div>
                                                   </div>
                                               </Col>
                                           </Row>
                                       </>
                                   )
                               }
                           </PaginationProvider>
                       </Col>
                   </Row>
   
                   <div className="ai-onboarding-tab-actions">
                       <Button
                           disabled={nextButtonIsDisabled}
                           color="primary"
                           onClick={onNextButtonClicked}
                       >
                           {t('Next')}
                       </Button>
   
                       <Button 
                           color="secondary" 
                           outline
                           onClick={onCancelButtonClicked}
                       >
                           {t('Cancel')}
                       </Button>
                   </div>
               </div>
           </>
       )

}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(ProcessingProducts))
)